import { Card, Eyebrow, StatusGlyph, cn, type StatusTone } from '@homecare/ui';
import type { ReactNode } from 'react';

/**
 * Who pays for the next visit, and how much is left.
 *
 * ## The question this answers
 *
 * A coordinator looking at a dual-payer client is about to schedule a visit.
 * They need one thing: **can I book this, and who is billed?** §7 is explicit
 * that they must not have to reason about `client_payer_rule` to get there, and
 * exit criterion 3 makes it a design requirement rather than a data one.
 *
 * So this is deliberately **not** the rule table with nicer borders. A table of
 * priorities, conditions and caps is the algorithm's input; showing it asks the
 * reader to execute the algorithm in their head, once per visit, correctly,
 * while somebody waits on the phone.
 *
 * ## What it shows instead
 *
 * The payers **in the order they absorb minutes**, which is the one fact the
 * ordering exists to express — and the fall-through named as the last row
 * rather than left implicit, because "and then what" is the question that
 * follows every cap.
 *
 * Three things per payer:
 *
 *   - **What it covers**, in words. "Weekdays before 6pm", not
 *     `applies_when: {dayOfWeek: [1..5], before: "18:00"}`.
 *   - **What is left**, as a bar, in units *and* hours. Payers authorise units;
 *     coordinators schedule hours. Showing one and making them convert is how
 *     a four-hour visit gets booked against three hours of remaining
 *     authorisation.
 *   - **The period it resets on**, because "28 units left" means something
 *     different on a Friday than on a Monday.
 *
 * And above them, one sentence that answers the scheduling question in the
 * general case — the thing a coordinator actually reads.
 *
 * ## The state that matters most
 *
 * When the last payer is capped and there is **no fall-through**, further
 * minutes are covered by nobody. That is not a styling variation; it is the
 * answer "no, not without somebody agreeing to pay privately", and it is the
 * case a table would render as an unremarkable row of numbers. It gets the
 * critical tone and says so in words.
 */

export interface CoverageSource {
  readonly id: string;
  readonly payerName: string;
  /** Already in words. The API renders `applies_when`; this only displays it. */
  readonly covers: string;
  /** Null for an uncapped arrangement — private pay usually is. */
  readonly authorizedUnits: number | null;
  readonly usedUnits: number;
  /** Minutes in one unit, so hours can be shown beside units. */
  readonly minutesPerUnit: number;
  /** "this week", "this month". Null when nothing resets. */
  readonly periodLabel: string | null;
  /** True for the `client.default_payer_id` row. */
  readonly isFallThrough: boolean;
  /** Derived authorization status, when this source has an authorization. */
  readonly status: StatusTone | null;
}

const hours = (units: number, minutesPerUnit: number): string => {
  const value = (units * minutesPerUnit) / 60;
  return `${Number.isInteger(value) ? value : value.toFixed(1)} ${value === 1 ? 'hour' : 'hours'}`;
};

/** Units left, or null when the source is uncapped. */
const remaining = (source: CoverageSource): number | null =>
  source.authorizedUnits === null ? null : Math.max(0, source.authorizedUnits - source.usedUnits);

/**
 * The sentence at the top.
 *
 * Composed rather than templated per case, because the interesting part is the
 * *join*: what covers the first minutes, what covers the rest, and whether
 * anything covers what is left after that.
 */
export function coverageSummary(sources: readonly CoverageSource[]): {
  sentence: string;
  tone: StatusTone;
} {
  if (sources.length === 0) {
    // Not "yet". This is reached two ways now — a client nobody ever set a
    // payer up for, and one whose arrangements have all lapsed — and "yet"
    // reads as the first, which would tell a coordinator the wrong story about
    // a client who was covered until last month.
    return {
      sentence: 'Nobody is set up to pay for this client’s visits.',
      tone: 'critical',
    };
  }

  const fallThrough = sources.find((source) => source.isFallThrough);
  const capped = sources.filter((source) => !source.isFallThrough);
  const exhausted = capped.filter((source) => remaining(source) === 0);

  if (fallThrough) {
    const live = capped.filter((source) => (remaining(source) ?? 0) > 0);

    if (live.length === 0) {
      return {
        sentence: `${fallThrough.payerName} covers every visit — no authorised units are left on anything else.`,
        tone: 'warning',
      };
    }

    const first = live[0]!;
    return {
      sentence:
        `${first.payerName} covers ${lowerFirst(first.covers)} until ` +
        `${remaining(first)} units run out ${first.periodLabel ?? ''}`.trim() +
        `, then ${fallThrough.payerName} covers the rest.`,
      tone: 'verified',
    };
  }

  if (exhausted.length === capped.length) {
    return {
      sentence:
        'Every authorisation is used up and there is no fall-through payer. ' +
        'Another visit would be covered by nobody.',
      tone: 'critical',
    };
  }

  const live = capped.filter((source) => (remaining(source) ?? 0) > 0);
  const first = live[0]!;

  return {
    sentence:
      `${first.payerName} covers ${lowerFirst(first.covers)} — ${remaining(first)} units left ` +
      `${first.periodLabel ?? ''}`.trim() +
      '. There is no fall-through payer, so anything past that is covered by nobody.',
    tone: 'warning',
  };
}

const lowerFirst = (value: string): string => value.charAt(0).toLowerCase() + value.slice(1);

const TONE_TEXT: Record<StatusTone, string> = {
  verified: 'text-verified',
  scheduled: 'text-scheduled',
  progress: 'text-progress',
  warning: 'text-warning',
  critical: 'text-critical',
  inactive: 'text-ink-3',
};

const TONE_SURFACE: Record<StatusTone, string> = {
  verified: 'bg-verified-bg border-verified-br text-verified',
  scheduled: 'bg-scheduled-bg border-scheduled-br text-scheduled',
  progress: 'bg-progress-bg border-progress-br text-progress',
  warning: 'bg-warning-bg border-warning-br text-warning',
  critical: 'bg-critical-bg border-critical-br text-critical',
  inactive: 'bg-inactive-bg border-inactive-br text-inactive',
};

const BAR_FILL: Record<StatusTone, string> = {
  verified: 'bg-verified',
  scheduled: 'bg-scheduled',
  progress: 'bg-progress',
  warning: 'bg-warning',
  critical: 'bg-critical',
  inactive: 'bg-inactive',
};

/** How full a source is, as a tone. Amber at 80% is §12's own threshold. */
function sourceTone(source: CoverageSource): StatusTone {
  const left = remaining(source);
  if (left === null) return 'verified';
  if (left === 0) return 'critical';

  const used = source.usedUnits / (source.authorizedUnits ?? 1);
  return used >= 0.8 ? 'warning' : 'verified';
}

export function CoveragePanel({ sources }: { sources: readonly CoverageSource[] }): ReactNode {
  const summary = coverageSummary(sources);

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-ink text-lg font-bold">Who pays</h2>
          <p className="text-ink-3 mt-1 text-sm">In the order visits are billed against them.</p>
        </div>
      </div>

      {/*
        The answer, before the detail. A coordinator who reads only this line
        should be able to schedule correctly — everything below is the working.
      */}
      <p
        className={cn(
          'text-md mt-4 flex items-start gap-2 rounded-md border px-4 py-3 font-medium',
          TONE_SURFACE[summary.tone],
        )}
      >
        <StatusGlyph
          name={
            summary.tone === 'verified'
              ? 'check'
              : summary.tone === 'critical'
                ? 'octagon'
                : 'triangle'
          }
          className="mt-0.5 h-4 w-4 flex-none"
        />
        {summary.sentence}
      </p>

      {sources.length === 0 ? null : (
        <ol className="mt-5 grid gap-4">
          {sources.map((source, index) => (
            <li key={source.id}>
              <CoverageRow source={source} order={index + 1} />
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}

function CoverageRow({ source, order }: { source: CoverageSource; order: number }): ReactNode {
  const left = remaining(source);
  const tone = sourceTone(source);
  const used = source.authorizedUnits === null ? 0 : source.usedUnits / source.authorizedUnits;

  return (
    <div className="border-line rounded-md border p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-ink text-md font-semibold">
          {/*
            The ordinal is the point of the whole panel: it is what says which
            payer absorbs the minutes first. Rendered as a number rather than
            left to reading order, because a list can be re-sorted by a table
            component and an ordinal cannot.
          */}
          <span className="text-ink-3 mr-2 font-mono text-sm" aria-hidden="true">
            {order}
          </span>
          {source.payerName}
          {source.isFallThrough && (
            <span className="text-ink-3 ml-2 text-sm font-normal">· fall-through</span>
          )}
        </p>

        <p className={cn('text-sm font-semibold', TONE_TEXT[tone])} data-numeric>
          {left === null ? (
            'No cap'
          ) : (
            <>
              {left} of {source.authorizedUnits} units left
              <span className="text-ink-3 ml-2 font-normal">
                ({hours(left, source.minutesPerUnit)})
              </span>
            </>
          )}
        </p>
      </div>

      <p className="text-ink-2 mt-1 text-sm">{source.covers}</p>

      {source.authorizedUnits !== null && (
        <div
          className="bg-canvas mt-3 h-2 w-full overflow-hidden rounded-full"
          role="img"
          aria-label={`${source.usedUnits} of ${source.authorizedUnits} units used${
            source.periodLabel ? ` ${source.periodLabel}` : ''
          }`}
        >
          <div
            className={cn('h-full rounded-full', BAR_FILL[tone])}
            style={{ width: `${Math.min(100, Math.round(used * 100))}%` }}
          />
        </div>
      )}

      {source.periodLabel && (
        <Eyebrow className="mt-2">
          {source.authorizedUnits === null ? source.periodLabel : `Resets ${source.periodLabel}`}
        </Eyebrow>
      )}
    </div>
  );
}
